import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Sparkles,
  Brain,
  Scale,
  Shield,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  HelpCircle,
  Database,
  Zap,
  Clock,
  BarChart3,
  MessageSquare,
  Eye,
  Lock,
  Unlock,
  RefreshCw,
  Star,
  Activity,
  Server,
  Cpu
} from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const AlfaIA = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('hybrid'); // 'hybrid', 'tribunal', 'memory'
  const [requireUnanimous, setRequireUnanimous] = useState(false);
  const [category, setCategory] = useState('general');
  
  // Results
  const [response, setResponse] = useState(null);
  const [tribunalVerdict, setTribunalVerdict] = useState(null);
  const [memoriesUsed, setMemoriesUsed] = useState(0);
  const [newMemories, setNewMemories] = useState(0);
  
  // Stats
  const [memoryStats, setMemoryStats] = useState(null);
  const [tribunalStats, setTribunalStats] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  
  // History
  const [history, setHistory] = useState([]);
  
  const messagesEndRef = useRef(null);

  const judges = [
    { name: 'Mistral Small 3.2', provider: 'Mistral • Open source', emoji: '', color: 'from-orange-500 to-amber-600', role: 'Analyste' },
    { name: 'Llama 4 Scout', provider: 'Meta • Open source', emoji: '', color: 'from-blue-500 to-indigo-600', role: 'Vérificateur' },
    { name: 'DeepSeek R1', provider: 'DeepSeek • Open source', emoji: '', color: 'from-cyan-500 to-teal-600', role: 'Arbitre' }
  ];

  const categories = [
    { value: 'general', label: 'Général', icon: '💬' },
    { value: 'security', label: 'Sécurité', icon: '🔒' },
    { value: 'finance', label: 'Finance', icon: '💰' },
    { value: 'operations', label: 'Opérations', icon: '⚙️' },
    { value: 'hr', label: 'RH', icon: '👥' },
    { value: 'legal', label: 'Juridique', icon: '⚖️' }
  ];

  const modes = [
    { 
      value: 'hybrid', 
      label: 'ALFA Hybride', 
      icon: Sparkles, 
      color: 'from-indigo-600 to-purple-600',
      desc: '3 LLM + Mémoire = IA Vivante'
    },
    { 
      value: 'tribunal', 
      label: 'Tribunal', 
      icon: Scale, 
      color: 'from-blue-600 to-cyan-600',
      desc: 'Consensus 3 juges'
    },
    { 
      value: 'memory', 
      label: 'SecurIA', 
      icon: Brain, 
      color: 'from-purple-600 to-pink-600',
      desc: 'Mémoire persistante'
    }
  ];

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [memRes, tribRes] = await Promise.all([
        api.get('/securia/memories/stats').catch(() => ({ data: null })),
        api.get('/tribunal/stats').catch(() => ({ data: null }))
      ]);
      
      setMemoryStats(memRes.data);
      setTribunalStats(tribRes.data);
      
      // Calculate system health
      const memScore = memRes.data?.totalMemories ? Math.min(100, memRes.data.totalMemories * 5) : 0;
      const tribScore = tribRes.data?.consensusRate || 0;
      setSystemHealth({
        score: Math.round((memScore + tribScore) / 2) || 50,
        memoryActive: !!memRes.data,
        tribunalActive: !!tribRes.data
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const processWithAlfa = async () => {
    if (!message.trim()) {
      toast({ title: 'Erreur', description: 'Entrez votre question', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setResponse(null);
    setTribunalVerdict(null);

    try {
      if (mode === 'hybrid' || mode === 'tribunal') {
        // Call Tribunal IA
        const tribunalResponse = await api.post('/tribunal/judge', {
          question: message,
          category,
          require_unanimous: requireUnanimous,
          context: mode === 'hybrid' ? 'Mode ALFA Hybride avec mémoire active' : null
        });
        
        setTribunalVerdict(tribunalResponse.data.verdict);
        
        // Also get memory context in hybrid mode
        if (mode === 'hybrid') {
          const memoryResponse = await api.post('/securia/chat', {
            message: `Contexte pour: ${message}`,
            includeMemory: true,
            includeSystemData: true
          });
          setMemoriesUsed(memoryResponse.data.memoriesUsed || 0);
          setNewMemories(memoryResponse.data.newMemories || 0);
        }
        
        toast({ 
          title: mode === 'hybrid' ? '🧬 ALFA a délibéré' : '⚖️ Tribunal terminé', 
          description: tribunalResponse.data.summary 
        });
      } else {
        // Memory-only mode (SecurIA)
        const memoryResponse = await api.post('/securia/chat', {
          message,
          includeMemory: true,
          includeSystemData: true
        });
        
        setResponse(memoryResponse.data.response);
        setMemoriesUsed(memoryResponse.data.memoriesUsed || 0);
        setNewMemories(memoryResponse.data.newMemories || 0);
        
        toast({ title: '🧠 SecurIA répond', description: 'Réponse avec mémoire active' });
      }
      
      // Add to history
      setHistory(prev => [{
        id: Date.now(),
        mode,
        question: message,
        timestamp: new Date().toISOString(),
        verdict: tribunalVerdict?.final_decision
      }, ...prev.slice(0, 9)]);
      
      fetchStats();
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: error.response?.data?.detail || 'Communication impossible', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'approve': case 'approved': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'reject': case 'rejected': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'need_info': return <HelpCircle className="w-5 h-5 text-yellow-500" />;
      default: return <AlertTriangle className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="p-4 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl animate-pulse">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1 animate-ping">
                <Activity className="w-3 h-3 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                ALFA
              </h1>
              <p className="text-slate-400">Intelligence Artificielle Hybride • 3 LLM + Mémoire</p>
            </div>
          </div>
          
          {/* System Status */}
          <div className="flex items-center gap-4">
            {systemHealth && (
              <div className="flex items-center gap-3 bg-slate-800/50 rounded-xl px-4 py-2">
                <Server className={`w-5 h-5 ${systemHealth.score > 70 ? 'text-green-400' : 'text-yellow-400'}`} />
                <div>
                  <p className="text-xs text-slate-400">Santé Système</p>
                  <p className="text-lg font-bold text-white">{systemHealth.score}%</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Architecture Visual */}
        <Card className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border-indigo-700/50 mb-6">
          <CardContent className="py-4">
            <div className="flex items-center justify-center gap-2 text-sm">
              <span className="px-3 py-1 bg-slate-800 rounded-full text-slate-300 flex items-center gap-1">
                <Shield className="w-4 h-4 text-blue-400" /> Garde-fous
              </span>
              <span className="text-slate-500">→</span>
              <span className="px-3 py-1 bg-gradient-to-r from-green-900/50 to-purple-900/50 rounded-full text-white flex items-center gap-1">
                🟢🟣🟠 3 LLM
              </span>
              <span className="text-slate-500">→</span>
              <span className="px-3 py-1 bg-purple-900/50 rounded-full text-purple-300 flex items-center gap-1">
                <Database className="w-4 h-4" /> Mémoire
              </span>
              <span className="text-slate-500">→</span>
              <span className="px-3 py-1 bg-gradient-to-r from-indigo-600 to-pink-600 rounded-full text-white font-bold">
                ✨ ALFA
              </span>
            </div>
          </CardContent>
        </Card>

        {/* 3 Judges Display */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {judges.map((judge, i) => (
            <Card key={i} className="bg-slate-800/50 border-slate-700 hover:border-slate-500 transition-all">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${judge.color} shadow-lg`}>
                    <Cpu className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-lg">{judge.emoji} {judge.name}</p>
                    <p className="text-xs text-slate-400">{judge.provider} • {judge.role}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Mode Selection */}
          <div className="grid grid-cols-3 gap-3">
            {modes.map((m) => (
              <Button
                key={m.value}
                variant={mode === m.value ? 'default' : 'outline'}
                onClick={() => setMode(m.value)}
                className={`h-auto py-4 flex flex-col items-center gap-2 ${
                  mode === m.value 
                    ? `bg-gradient-to-r ${m.color} border-0` 
                    : 'border-slate-700 bg-slate-800/50'
                }`}
              >
                <m.icon className="w-6 h-6" />
                <span className="font-bold">{m.label}</span>
                <span className="text-xs opacity-80">{m.desc}</span>
              </Button>
            ))}
          </div>

          {/* Input Card */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                {mode === 'hybrid' ? <Sparkles className="w-5 h-5 text-pink-400" /> :
                 mode === 'tribunal' ? <Scale className="w-5 h-5 text-blue-400" /> :
                 <Brain className="w-5 h-5 text-purple-400" />}
                {mode === 'hybrid' ? 'Interroger ALFA' :
                 mode === 'tribunal' ? 'Soumettre au Tribunal' :
                 'Demander à SecurIA'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Category Selection */}
              {(mode === 'hybrid' || mode === 'tribunal') && (
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Catégorie</label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <Button
                        key={cat.value}
                        variant={category === cat.value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCategory(cat.value)}
                        className={category === cat.value ? 'bg-indigo-600' : 'border-slate-600 bg-slate-700/50'}
                      >
                        {cat.icon} {cat.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Question Input */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">
                  {mode === 'hybrid' ? 'Question pour ALFA (3 IA + mémoire)' :
                   mode === 'tribunal' ? 'Question pour le Tribunal (3 juges)' :
                   'Question pour SecurIA (avec mémoire)'}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={mode === 'hybrid' 
                    ? 'ALFA analysera avec 3 cerveaux synchronisés et sa mémoire...'
                    : 'Décrivez votre question...'}
                  className="w-full h-32 bg-slate-700 border-slate-600 rounded-lg p-3 text-white placeholder:text-slate-500 resize-none"
                />
              </div>

              {/* Options */}
              <div className="flex items-center justify-between">
                {(mode === 'hybrid' || mode === 'tribunal') && (
                  <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={requireUnanimous}
                      onChange={(e) => setRequireUnanimous(e.target.checked)}
                      className="rounded border-slate-600"
                    />
                    {requireUnanimous ? <Lock className="w-4 h-4 text-yellow-400" /> : <Unlock className="w-4 h-4" />}
                    Exiger l'unanimité (3/3)
                  </label>
                )}
                {mode === 'memory' && (
                  <span className="text-sm text-purple-400 flex items-center gap-1">
                    <Database className="w-4 h-4" />
                    {memoryStats?.totalMemories || 0} souvenirs en mémoire
                  </span>
                )}

                <Button
                  onClick={processWithAlfa}
                  disabled={loading || !message.trim()}
                  className={`bg-gradient-to-r ${
                    mode === 'hybrid' ? 'from-indigo-600 via-purple-600 to-pink-600' :
                    mode === 'tribunal' ? 'from-blue-600 to-cyan-600' :
                    'from-purple-600 to-pink-600'
                  } hover:opacity-90`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {mode === 'hybrid' ? 'ALFA délibère...' : mode === 'tribunal' ? 'Délibération...' : 'Réflexion...'}
                    </>
                  ) : (
                    <>
                      {mode === 'hybrid' ? <Sparkles className="w-4 h-4 mr-2" /> :
                       mode === 'tribunal' ? <Scale className="w-4 h-4 mr-2" /> :
                       <Send className="w-4 h-4 mr-2" />}
                      {mode === 'hybrid' ? 'Interroger ALFA' : mode === 'tribunal' ? 'Soumettre' : 'Demander'}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tribunal Verdict Display */}
          {tribunalVerdict && (
            <Card className={`border-2 ${
              tribunalVerdict.final_decision === 'approved' ? 'bg-green-900/20 border-green-500' :
              tribunalVerdict.final_decision === 'rejected' ? 'bg-red-900/20 border-red-500' :
              'bg-yellow-900/20 border-yellow-500'
            }`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-white">
                    {mode === 'hybrid' ? <Sparkles className="w-5 h-5 text-pink-400" /> : <Scale className="w-5 h-5" />}
                    {mode === 'hybrid' ? 'Verdict ALFA' : 'Verdict du Tribunal'}
                  </span>
                  <span className="text-sm font-normal text-slate-400 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {tribunalVerdict.totalTimeMs}ms
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Final Decision */}
                <div className={`p-4 rounded-lg ${
                  tribunalVerdict.final_decision === 'approved' ? 'bg-green-900/30' :
                  tribunalVerdict.final_decision === 'rejected' ? 'bg-red-900/30' :
                  'bg-yellow-900/30'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xl font-bold text-white">
                      {tribunalVerdict.final_decision === 'approved' && '✅ APPROUVÉ'}
                      {tribunalVerdict.final_decision === 'rejected' && '❌ REJETÉ'}
                      {tribunalVerdict.final_decision === 'disputed' && '⚠️ DISPUTÉ'}
                      {tribunalVerdict.final_decision === 'need_info' && '❓ INFOS REQUISES'}
                    </span>
                    <span className="text-3xl font-bold text-white">
                      {Math.round(tribunalVerdict.confidence_score * 100)}%
                    </span>
                  </div>
                  <p className="text-sm text-slate-300">
                    {tribunalVerdict.unanimous ? '🌟 UNANIMITÉ' : 
                     `Vote: ${tribunalVerdict.votes?.approve || 0}✅ ${tribunalVerdict.votes?.reject || 0}❌ ${tribunalVerdict.votes?.need_info || 0}❓`}
                  </p>
                  {mode === 'hybrid' && memoriesUsed > 0 && (
                    <p className="text-sm text-purple-400 mt-2 flex items-center gap-1">
                      <Database className="w-4 h-4" />
                      {memoriesUsed} souvenirs utilisés pour contextualiser
                    </p>
                  )}
                </div>

                {/* Individual Judges */}
                <div className="space-y-3">
                  <p className="text-sm text-slate-400 font-medium">Avis des 3 juges :</p>
                  {tribunalVerdict.judges?.map((judge, i) => (
                    <div key={i} className="bg-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{judges[i]?.emoji}</span>
                          <span className="font-medium text-white">{judge.name}</span>
                          <span className="text-xs text-slate-400 bg-slate-800 px-2 py-0.5 rounded">{judge.role}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getDecisionIcon(judge.decision)}
                          <span className="text-sm text-slate-300">{Math.round((judge.confidence || 0) * 100)}%</span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-300 line-clamp-3">
                        {judge.reasoning || judge.response?.substring(0, 300)}...
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Memory Response Display */}
          {response && mode === 'memory' && (
            <Card className="bg-purple-900/20 border-purple-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Brain className="w-5 h-5 text-purple-400" />
                  Réponse SecurIA
                  {memoriesUsed > 0 && (
                    <span className="text-xs bg-purple-900/50 px-2 py-0.5 rounded text-purple-300 ml-2">
                      🧠 {memoriesUsed} souvenirs utilisés
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-200 whitespace-pre-wrap">{response}</p>
                {newMemories > 0 && (
                  <div className="mt-4 text-sm text-green-400 flex items-center gap-1">
                    <Database className="w-4 h-4" />
                    +{newMemories} nouveau(x) souvenir(s) mémorisé(s)
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-400" />
                Statistiques ALFA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Memory Stats */}
              <div className="bg-purple-900/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-purple-300 font-medium">Mémoire</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{memoryStats?.totalMemories || 0}</p>
                    <p className="text-xs text-slate-400">Souvenirs</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{memoryStats?.recentlyAccessed || 0}</p>
                    <p className="text-xs text-slate-400">Récents</p>
                  </div>
                </div>
              </div>

              {/* Tribunal Stats */}
              <div className="bg-blue-900/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Scale className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-blue-300 font-medium">Tribunal</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{tribunalStats?.total || 0}</p>
                    <p className="text-xs text-slate-400">Verdicts</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-400">{tribunalStats?.consensusRate || 0}%</p>
                    <p className="text-xs text-slate-400">Consensus</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Why ALFA */}
          <Card className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border-indigo-700/50">
            <CardContent className="pt-6">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Pourquoi ALFA ?
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <div>
                    <strong className="text-white">3 cerveaux</strong>
                    <p className="text-slate-400">Mistral + Llama + DeepSeek (open source)</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <div>
                    <strong className="text-white">Mémoire vivante</strong>
                    <p className="text-slate-400">Se souvient de TOUT</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <div>
                    <strong className="text-white">Garde-fous</strong>
                    <p className="text-slate-400">Sécurité maximale</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <div>
                    <strong className="text-white">Vote majoritaire</strong>
                    <p className="text-slate-400">2/3 = décision fiable</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-400" />
                Activité récente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucune activité</p>
              ) : (
                <div className="space-y-2">
                  {history.slice(0, 5).map((item, i) => (
                    <div key={item.id} className="bg-slate-700/50 rounded p-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="truncate flex-1 text-slate-300">{item.question?.substring(0, 25)}...</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          item.mode === 'hybrid' ? 'bg-pink-900/50 text-pink-400' :
                          item.mode === 'tribunal' ? 'bg-blue-900/50 text-blue-400' :
                          'bg-purple-900/50 text-purple-400'
                        }`}>
                          {item.mode === 'hybrid' ? '🧬' : item.mode === 'tribunal' ? '⚖️' : '🧠'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AlfaIA;
